import { consume } from '@lit/context';
import { LitElement, css, html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import { LinkifyIt } from 'linkify-it';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { scrollbarThinHover } from '../../../theme/scrollbar.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

// linkify-it 6 flipped two defaults that were on in 5: `fuzzyLink` (bare
// domains like github.com) and `urlAuth` (without it, http://user:pass@host
// truncates to a bogus http://user link). Both restored for parity.
const linkify = new LinkifyIt({ fuzzyLink: true, urlAuth: true });

/**
 * Debug Popup - Displays formatted JSON from server command replies
 *
 * Shown when a `/server` or other server command returns non-type-0 data.
 * Renders a centered overlay with selectable, formatted JSON and a close
 * button; Escape, a tap on the game, or opening a panel also closes it.
 *
 * @element debug-popup
 */
@customElement('debug-popup')
export class DebugPopup extends LitElement {
  static override styles = [
    tokens,
    scrollbarThinHover,
    css`
      :host {
        display: block;
        pointer-events: auto;
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .popup {
        position: fixed;
        /* z-index lives here, not on :host — the host is not positioned so its
           z-index is ignored. The panel tier (see airmash-ui's z ladder): a
           typed command opens it, like /emotes' panel. At 50 it sat under the
           toolbar (150), so in landscape a tap on its X opened the menu, and
           under the mobile chat drawer (201) the command was typed in. */
        z-index: 300;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        /* Cap to the viewport so the box can't clip offscreen on small / short /
           zoomed windows and phones; scrolls internally past the cap. dvh
           handles the iOS URL-bar collapse, and the insets keep the X out of a
           landscape notch. */
        width: min(800px, calc(100vw - 32px - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)));
        height: min(600px, calc(100vh - 32px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)));
        height: min(600px, calc(100dvh - 32px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)));
        background-color: var(--color-bg-panel);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        box-sizing: border-box;
        padding: 18px;
        overflow: hidden;
      }

      .text {
        overflow: auto;
        font-weight: bold;
        font-family: Courier, monospace;
        color: #f0f0f0;
        font-size: 15px;
        /* text, not legacy's auto: auto resolves to none under a parent that
           has none (this host, and the body), so the JSON could never be
           selected or copied. */
        -webkit-user-select: text;
        user-select: text;
        width: 100%;
        height: 100%;
        white-space: pre;
        /* Long lines scroll sideways too; keep the gesture in the box. */
        touch-action: pan-x pan-y;
        overscroll-behavior: contain;
      }

      .text a {
        color: var(--color-link-url);
      }

      .close {
        position: absolute;
        width: 16px;
        height: 16px;
        background: url('/vanilla/ui/chat/x.svg') no-repeat center / 16px 16px;
        top: 18px;
        right: 18px;
        cursor: pointer;
        opacity: 0.4;
      }

      @media (hover: hover) {
        .close:hover {
          opacity: 1;
        }
      }

      /* A 16px X is below the 24px touch minimum: pad the hit area to 44px,
         invisibly. It stays inside the popup's 18px padding, so overflow
         doesn't clip it. */
      @media (hover: none) {
        .close::after {
          content: '';
          position: absolute;
          inset: -14px;
        }
      }
    `,
  ];

  private state = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  // Memoize autolinked text to avoid re-running linkify on every render
  private cachedJson = '';
  private cachedParts: (TemplateResult | string)[] = [];

  override render() {
    const popup = this.state.debugPopup;
    if (!popup) return nothing;

    if (popup.json !== this.cachedJson) {
      this.cachedJson = popup.json;
      this.cachedParts = this.renderAutolinkedText(popup.json);
    }

    return html`
      <div class="popup" @contextmenu=${this.handleContextMenu}>
        <div class="text scrollbar-thin-hover">${this.cachedParts}</div>
        <div class="close" @click=${this.handleClose}></div>
      </div>
    `;
  }

  /** Splits text into plain text and clickable URL segments. URLs render in
   *  full (no truncation) — this is a debug JSON viewer, so the displayed text
   *  must stay faithful to the raw payload for reading and copy/paste. */
  private renderAutolinkedText(text: string): (TemplateResult | string)[] {
    const matches = linkify.match(text);
    if (!matches) return [text];

    const parts: (TemplateResult | string)[] = [];
    let lastIndex = 0;

    for (const m of matches) {
      if (m.index > lastIndex) {
        parts.push(text.substring(lastIndex, m.index));
      }
      parts.push(html`<a target="_blank" rel="noopener noreferrer" href=${m.url}>${m.text}</a>`);
      lastIndex = m.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  }

  /** Prevent context menu from propagating (preserves original behavior) */
  private handleContextMenu = (e: MouseEvent) => {
    e.stopPropagation();
  };

  /** The X plays the click; Escape and outside taps close it silently through
   *  closeAllPanels. */
  private handleClose = () => {
    this.api.playClick();
    this.api.closeDebugPopup();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'debug-popup': DebugPopup;
  }
}
